import React, { Component } from "react";
import { injectIntl } from "react-intl";
import ReactTooltip from "react-tooltip";
import moment from "moment";

import "wxrecall/components/App.css";

import {
  NetworkErrorIndicatorComponent,
  NetworkLoadingIndicatorComponent
} from "workspace/components/NetworkIndicators";

import NetworkManager from "wxrecall/util/networkManager";
import SearchResults from "wxrecall/components/SearchResults";
import SearchInput from "wxrecall/components/SearchInput";
import { formatBulletin, formatWindData } from "wxrecall/util/formatters";
import {
  expandPosition,
  normalizeData,
  translateData,
  getLocation
} from "wxrecall/components/SearchResults/utils";
import { getClone } from "wxrecall/util/util";
import DefaultStates from "wxrecall/util/DefaultStates";


export const AppContext = React.createContext({
  language: "english",
  mobile: false
});

// Some constants to track if we are displaying on a phone or not.
let mobile = false;
const mobilePhone = window.isMobile.phone;
const userPreference = window.settings.mobilePref;

const LOCATION_BUFFER = 2 // Used for upper wind table formatted, creates this many extra spaces to the right of the location name
const DATA_BUFFER = 2 // Used for upper wind table formatting, creates this many extra spaces on both sides of the weather data

function forceMobile() {
  mobile = true;
  document.body.classList.add("mobile");

  document.addEventListener("scroll", e => {
    const classList = document.getElementsByClassName("scroll-to-top-button")[0]
      .classList;
    if (window.scrollY > 550) {
      classList.remove("hide");
    } else {
      classList.add("hide");
    }
  });
}

function forceDesktop() {
  document.body.classList.add("desktop");
}

if (userPreference === "mobile") {
  forceMobile();
} else if (userPreference === "desktop") {
  forceDesktop();
} else if (mobilePhone) {
  forceMobile();
} else {
  forceDesktop();
}

// HELPER FUNCTIONS
// TODO: maybe move elsewhere?

function formatDateString(dateString) {

  // strips useful information from upperwind date strings
  const day = dateString.substring(8, 10);
  const hours = dateString.substring(11, 13);
  const minutes = dateString.substring(14, 16);

  return { day, hours, minutes };
}

function parseText(text) {
  const parsedArray = JSON.parse(text);
  const valid = parsedArray[4];
  const for_use_start = parsedArray[5];
  const for_use_end = parsedArray[6];
  const altitudes = parsedArray[11];

  // Format the valid field as DDHHMMZ
  const validFormatted = formatDateString(valid);
  const formattedValid = `${validFormatted.day}${validFormatted.hours}${validFormatted.minutes}Z`;

  // Extract hours from timestamps
  const forUseStart = formatDateString(for_use_start);
  const forUseEnd = formatDateString(for_use_end);
  const for_use = `${forUseStart.hours}-${forUseEnd.hours}`;

  // Extract month from valid timestamp and convert to full month name
  const date = new Date(valid);
  const month = date.toLocaleString('default', { month: 'long' });

  return { valid: formattedValid, for_use, altitudes, month, valid_date:moment.utc(valid)};
}

function padText(text, width) {
  text = !!text ? text : ''
  const padding = width - text.length;
  const paddingLeft = Math.ceil(padding / 2);
  const paddingRight = padding - paddingLeft;
  return ' '.repeat(paddingLeft) + text + ' '.repeat(paddingRight);
}

function determineAltitude(record) {
  const parsedText = JSON.parse(record.text);
  const altitudesArray = parsedText[11];
  const hasHighAltitude = altitudesArray.some(altitude => altitude[0] > 29999);
  return hasHighAltitude ? 'HIGH' : 'LOW';
}

// TABLE FORMATING
// Now with 110.2% more lines >:)
export function createTableString(parsedData, intl) {
  /* Buckle in, this function is a doozy...
     This function takes a complex dictionary of parsed upper wind data grouped by valid date, then by location, and finally by altitude.
     The data is then reformatted into a new text-based table containing every upper wind entry.

     The final result should look something like this:

     VALIDE 111800Z AUX FINS D’UTILISATION 12-00
               3000    |    6000    |    9000    |   12000    |   18000    
     YOW     60 11     |       12   |  250 8 8   |  240 17 4  | 230 20 -6

     VALIDE 111800Z AUX FINS D’UTILISATION 12-00
              24000    |   30000    |   34000    |   39000    |   45000    |   53000    
     YOW    250 34 -18 | 240 82 -29 | 240 93 -37 | 230 95 -48 | 240 70 -60 | 230 22 -61
  */

  // This will be used to store the final formatted wind table for display
  let tableString = '';

  const translated_for_use = intl.formatMessage({id: 'FOR USE'})
  const translated_valid = intl.formatMessage({id: 'VALID'})

  const data = {}; // Contains the flattened row data extracted from the parsed data groupings
  let locations = []; // Used further down to determine how much space is needed in the first column
  let altitudes = new Set(); // Used in conjunction with maxDataLength to determine the size of columns
  let maxDataLength = 0; // keeps track of the longest data string for each row so we can properly set the size of each column

  // This loop iterates over each valid/for-use group, then each location, and finally each altitude. The function will
  // convert the altitude data into a flat string, formatting as needed for positive temperature values, and replacing null
  // values with blank spaces.
  parsedData.forEach((datum) => {
    if (!data[datum.valid_date]){
      data[datum.valid_date] = {}
    }
    const locationData = data[datum.valid_date]
    locationData['for_use'] = datum.for_use
    locationData['valid'] = datum.valid
    if(!locationData['locations']){
      locationData['locations'] = {}
    }
    const locationDict = locationData['locations']
    if (!locationDict[datum.location]){
      locationDict[datum.location] = {};
    }
    if (!locations.includes(datum.location)){
      locations.push(datum.location)
    }
    const altitudeData = locationDict[datum.location];
    datum.altitudes.forEach((altitudeDatum) => {
      // altitudeDatum format: [altitude, direction, speed, temperature, isError]
      const [altitude, direction, speed, temperature, isError] = altitudeDatum;
      const formattedDatum = formatWindData(direction, speed, temperature, isError, intl.formatMessage);
      altitudeData[altitudeDatum[0]] = formattedDatum
      if (formattedDatum.length > maxDataLength){
        maxDataLength = formattedDatum.length
      }
      altitudes.add(altitudeDatum[0])
    });
  });

  // Cheesy duplicate removal because set() doesn't guarantee order
  locations = locations.filter((value, index) => locations.indexOf(value) === index)
  // Non-cheesy duplicate removal because we can easily resort afterwards
  altitudes = [...new Set(altitudes)].sort((a,b) => a-b)


  // Calculate column sizes so we make sure ensure everything lines up nicely based on the largest column data
  const maxLocationSize = Math.max('LOC'.length, ...locations.map(item => item.length)) + LOCATION_BUFFER
  const maxAltitudeLength = altitudes[altitudes.length - 1].toString().length // Since the list is sorted, the last altitude will always be the longest
  // This handy math trick ensures the max data length is upped to the nearest even number, which ensures that there is equal spacing before
  // and after the data itself. The longest data will end having exactly one space between the data and the pipe symbols. For example:
  // | 250 80 -25 |
  maxDataLength = 2 * Math.round((Math.max(maxAltitudeLength, maxDataLength) + DATA_BUFFER)/2) 

  const altitudeHeaders = altitudes.map(altitude => padText(altitude.toString(), maxDataLength)).join("|")

  // Iterate over the flattened data, sorting in ASCENDING (chronological) order (aka oldest to newest)
  // By the time execution reaches here, it is expected that most of the formatting is already done, save for any padding
  // that coulnd't be done until now (meaning altitude rows are already flattened, signs added, etc).
  for (const [validDate, validityData] of Object.entries(data).sort((a,b) => moment(a[0]) - moment(b[0]))){
    const tableTitle = `${translated_valid} ${validityData.valid} ${translated_for_use} ${validityData.for_use}`;
    tableString += `${tableTitle}\n${padText('', maxLocationSize+1)}${altitudeHeaders}\n`

    const locationData = validityData.locations;
    for (const location of locations){
      const altitudeData = locationData[location]
      if (!altitudeData) continue
      const rowData = []
      for (const altitude of altitudes){
        const altitudeDatum = altitudeData[altitude]
        rowData.push(padText(altitudeDatum, maxDataLength))
      }
      tableString += `${location.padEnd(maxLocationSize)} ${rowData.join("|")}\n`
    }
    tableString += '\n'
  }

  return tableString.trim();
}

/**
 * Top most application component for all of wxrecall. The magic starts here.
 */
export class App extends Component {
  constructor() {
    super();
    this.state = {
      selectedFilters: [],
      searchData: {},
      message: null,
      tab: "weather",
      networkRequestStates: {},
      preferredLanguage: null,
      collapseDuplicates: null,
      sortOrder: DefaultStates.sortOrder.weather
    };

    this.networkManager = new NetworkManager(
      this.updateRequestState.bind(this)
    );

    this.addMessage = this.addMessage.bind(this);
  }

  updateRequestState = (type, property, value) => {
    // This just sets this component's record of what state each of the request types is in. Used
    // for displaying network indicators
    this.setState({
      networkRequestStates: {
        ...this.state.networkRequestStates,
        [type]: {
          ...this.state.networkRequestStates[type],
          [property]: value
        }
      }
    });
  };

  /**
   * Retrieve meta info from data object
   * @param {object} data - Data received from server
   * @param {object} state - State of component (language)
   * @returns {object} - Extracted meta info
   */
  getMeta = (data, state) => ({
    isRoute: data.meta.route,
    tags: data.searchParameters.tags,
    now: data.meta && data.meta.now ? moment.utc(data.meta.now) : undefined,
    displayNotamQField: window.DISPLAY_NOTAM_Q_LINE || false,
    networkManager: this.networkManager,
    language: state.preferredLanguage
  });

  /**
   * Process processes a set of records
   * @param {object} record - records to be processed
   * @param {object} meta  - meta information
   * @param {object} intl - internalization data
   * @returns {array} - Array of processed records
   */
  processRecord = (record, meta, intl) => {
    const processedRecords = [];
    for (let tempRecord of expandPosition(record)) {
      translateData(tempRecord, meta, intl);
      formatBulletin(tempRecord, meta, intl);
      normalizeData(tempRecord);
      processedRecords.push(tempRecord);
    }
    return processedRecords;
  };

  /**
   * Called when data is received from the server after the user initiates a search.
   * Processes and massages the data in preparation for filtration and display. Sets
   * the application state with this massaged data.
   * @param  {[object]} data Data object received from the server containing weather data
   */
  onSearchResultsReceived = data => {
    const translatedData = [];
    const { tab, preferredLanguage } = this.state;
    const { intl } = this.props;

    if (data.data) {
      // Grab our meta info
      const meta = this.getMeta(data, this.state);
      const groupedMetars = {};
      const groupedUpperWinds = []
      // Process and translate each record
      data.data.forEach(record => {
        if (tab === "weather") {
          translatedData.push(...this.processRecord(record, meta, intl));
        } else {
          const tempRecord = getClone(record);
          translateData(tempRecord, meta, intl);
          formatBulletin(tempRecord, meta, intl);
          normalizeData(tempRecord);
          translatedData.push(tempRecord);
        }
      });

      let containsUpperWinds = false

      // Group METARs into a single list entry based on location (e.g., CYEG)
      // Group Upperwinds into 2 lists based on pointReference and radialDistance
      // for single point, single point with radius, and multiple points without
      // radius (aka discrete points),
      // and solely pointReference for multiple points with radius (aka route)
      translatedData.sort((a, b) => {
        if (typeof a.pointReference === 'string') {
          const str_diff = meta.tags.indexOf(a.pointReference) - meta.tags.indexOf(b.pointReference)
          return str_diff + (a.radialDistance - b.radialDistance)
        } else{
          return a.pointReference - b.pointReference
        }
      })
      translatedData.forEach(record => {
        if (record.type === "upperwind") {
          containsUpperWinds = true
          var numericPointReference = record.pointReference
          if (record.pointReference instanceof String) {
            numericPointReference = meta.tags.indexOf(record.pointReference)
          }
          const parsedDatum = {
            ...parseText(record.text),
            location: record.location,
            pointReference: record.pointReference
          }
          groupedUpperWinds.push(parsedDatum)
        }
        if (record.type === "metar") {
          if (!groupedMetars[record.location]) {
            groupedMetars[record.location] = [];
          }
          groupedMetars[record.location].push(record);
        }
      });

      // METAR LOGIC
      // Concatenate the METARS text fields for each location
      const concatenatedMetars = {};
      for (const location in groupedMetars) {
        const recordsWithSameLocation = groupedMetars[location];
        const concatenatedText = recordsWithSameLocation
          .map(record => record.formattedBulletin)
          .join('\n');

        // Maintain other fields
        const baseRecord = recordsWithSameLocation[0];
        concatenatedMetars[location] = {
          ...baseRecord,
          formattedBulletin: concatenatedText,
          text: concatenatedText,
        };
      }

      // Remove old METAR entries and add newly formed METAR records to filteredData
      const filteredData = translatedData.filter(record => record.type !== "metar" && record.type !== "upperwind");

      let upperWindRecord = null;
      if(containsUpperWinds){
        // UPPERWIND LOGIC
        const upperWindTables = createTableString(groupedUpperWinds, intl)

        // Get the first upper wind record so we can clone it and create a new 'fake' record that will contain the combined table
        upperWindRecord = JSON.parse(JSON.stringify(translatedData.find(record => record.type === "upperwind")));
        upperWindRecord.text = upperWindTables
        upperWindRecord.formattedBulletin = upperWindTables
        upperWindRecord.pointReference = ''
        upperWindRecord.location = ''
        upperWindRecord.position.pointReference = ''
        upperWindRecord.position.radialDistance = 0
        upperWindRecord.radialDistance = 0

        // Add the concatenated upperwind record
        filteredData.push(upperWindRecord)
      }

      // Add the concatenated METAR records
      for (const location in concatenatedMetars) {
        filteredData.push(concatenatedMetars[location]);
      }

      data.data = filteredData;
    } else {
      data.data = translatedData;
    }

    // Set the new results data and reset the filters
    this.setState({ searchData: data, filtered: [], selectedFilters: [] });
  };

  /**
   * Adds a message to be displayed on screen for the message.timeout amount of time
   * (or default to 5 seconds).
   * @param  {[object]} message A message object to be displayed
   */
  addMessage = message => {
    // Clear out any pre-existing message timeouts
    if (this.state.message) {
      // If this is already passed or non-existent, it will just fail silently
      clearTimeout(this.state.message.timeout);
    }

    // Set a timeout to clear this message, as this is just a temporary message
    message.timeout = setTimeout(
      function () {
        this.setState({
          message: {
            ...this.state.message,
            fade: true
          }
        });
      }.bind(this),
      message.timeout || 5000
    );
    this.setState({ message: message });
  };

  setSelectedFilters = selectedFilters => {
    this.setState({
      selectedFilters: selectedFilters
    });
  };

  onFiltered = filtered => {
    this.setState({ filtered });
    return filtered;
  };

  toggleTab = tab => {
    this.setState({ tab: tab });
  };

  restoreDefaults() {
    this.setState({
      sortOrder: DefaultStates.sortOrder.weather
    })
  };

  onSort(sortOrder) {
    this.setState({sortOrder:sortOrder})
  };

  render() {
    let message = null;
    if (this.state.message) {
      let className = "search-messages alert";

      // Use whatever className might've been passed to us
      className += this.state.message.type
        ? ` alert-${this.state.message.type}`
        : " alert-success";

      // If we should fade out this message, add the class for it
      className += this.state.message.fade ? " fade" : "";
      message = (
        <div className={className}>
          <h4>{this.state.message.text}</h4>
        </div>
      );
    }

    return (
      <AppContext.Provider
        value={{ language: this.state.preferredLanguage, mobile }}
      >
        <div id="search-container">
          {message}
          <SearchInput
            networkManager={this.networkManager}
            updateRequestState={this.updateRequestState}
            searchCallback={this.onSearchResultsReceived.bind(this)}
            addMessage={this.addMessage}
            tab={this.state.tab}
            toggleTab={this.toggleTab.bind(this)}
            setPreferredLanguage={e => this.setState({ preferredLanguage: e })}
            setCollapseDuplicates={e =>
              this.setState({ collapseDuplicates: e })
            }
            restoreDefaults={this.restoreDefaults.bind(this)}
          />
          <SearchResults
            searchData={this.state.searchData}
            sortOrder={this.state.sortOrder}
            onSort={this.onSort.bind(this)}
            tab={this.state.tab}
            networkManager={this.networkManager}
            collapseDuplicates={this.state.collapseDuplicates}
            selectedFilters={this.state.selectedFilters}
            setSelectedFilters={this.setSelectedFilters}
          />
          <NetworkErrorIndicatorComponent
            networkTransactions={this.state.networkRequestStates}
          />
          <NetworkLoadingIndicatorComponent
            networkTransactions={this.state.networkRequestStates}
          />
          <ReactTooltip effect="solid" place="bottom" />
        </div>
      </AppContext.Provider>
    );
  }
}

export default injectIntl(App);
